// Gate A1.3's test scene, as data and systems (no three.js): a walled pen of rolling ground, a plinth with crate
// stacks, a few static shapes, a pile of dynamic props, and one kinematic "pusher" driven by the recorded input, so the
// input changes the outcome. All numbers here are the game's values, passed into the engine's packages.
//
// Each sim step: the host turns the `move` stick into the pusher's intent, then ticks the kernel game, whose systems
// run in order: the pusher (accelerate, probe the ground with a ray, write the kinematic target), the wind (a seeded
// shove every half second), the physics step. The kernel holds the pusher's and the wind's state; Rapier holds the
// props'. Since A2 this is the lab scene (`?scene=lab`) of the spike's one kernel game: its module joins the core's.
import type { ContentPack, GameModule } from "@rpg/kernel";
import type { ActionMap, RngStreams } from "@rpg/runtime";
import type { BodySpec, CollisionWorld, LayerTable, PrimitiveShape, Vec3 } from "@rpg/collision";
import type { GameEvents } from "../core";
import type { SceneDef } from "./host";

/** Planted defects for the replay spec (tests/replay.spec.ts). */
export const SIM_FAULTS = {
  /** The wind draws from Math.random instead of its seeded stream: replays and restored saves diverge. */
  mathRandom: false,
};

/** Collision layers: the ground (terrain, walls, static shapes), loose props and the pusher. */
export const LAYERS: LayerTable = { ground: [], prop: ["ground", "prop", "pusher"], pusher: [] };

/** The sim's seed and its RNG stream names. */
export const SIM_SEED = 0x6a1e;
export const WIND_STREAM = "wind";

const PEN = 15; // half-size of the walled pen, m
const FIELD = 32; // the heightfield's extent, m
const CELLS = 32;

/** The rolling ground's height at (x, z): low hills, flattened under the plinth. */
export function groundHeight(x: number, z: number): number {
  const h = 0.35 + 0.25 * Math.sin(x * 0.45) * Math.cos(z * 0.38) + 0.12 * Math.sin(x * 0.9 + z * 0.7);
  const flat = Math.min(1, Math.max(0, (Math.hypot(x, z) - 3) / 3)); // 0 under the plinth, 1 from 6 m out
  return 0.3 + (h - 0.3) * flat;
}

/** Pusher tuning. */
const PUSHER = { speed: 5, accel: 8, radius: 0.5, halfHeight: 0.5, start: { x: 0, z: 6 } };
/** Wind: a shove on one random prop every `every` steps. */
const WIND = { every: 60, impulse: 1.6 };

/** The pusher's kernel component. */
export interface Pusher {
  x: number;
  z: number;
  vx: number;
  vz: number;
}
/** The wind's kernel component: steps until the next shove, and how many shoves so far. */
export interface Wind {
  wait: number;
  shoves: number;
}
/** Links a kernel entity to its collision body. */
export interface Physical {
  body: string;
}

export const PUSHER_C = "sim/Pusher";
export const WIND_C = "sim/Wind";
export const PHYSICAL_C = "sim/Physical";
/** The component types the state hash covers. */
export const HASHED = [PUSHER_C, WIND_C, PHYSICAL_C] as const;

/** Every body the scene starts with (the content pack adds them; the view draws them). */
export function sceneBodies(): BodySpec[] {
  const out: BodySpec[] = [];
  const heights: number[] = [];
  for (let iz = 0; iz <= CELLS; iz++) for (let ix = 0; ix <= CELLS; ix++) heights.push(groundHeight((ix / CELLS - 0.5) * FIELD, (iz / CELLS - 0.5) * FIELD));
  out.push({ id: "ground", kind: "fixed", colliders: [{ shape: { type: "heightfield", rows: CELLS, cols: CELLS, heights, size: { x: FIELD, y: 1, z: FIELD } }, layer: "ground", tag: "grass" }] });
  // the pen's walls
  for (const [id, x, z, hx, hz] of [
    ["wall-n", 0, -PEN, PEN, 0.3],
    ["wall-s", 0, PEN, PEN, 0.3],
    ["wall-w", -PEN, 0, 0.3, PEN],
    ["wall-e", PEN, 0, 0.3, PEN],
  ] as const) {
    out.push({ id, kind: "fixed", position: { x, y: 0.8, z }, colliders: [{ shape: { type: "cuboid", halfExtents: { x: hx, y: 0.8, z: hz } }, layer: "ground", tag: "stone" }] });
  }
  out.push({ id: "plinth", kind: "fixed", position: { x: 0, y: 0.6, z: 0 }, colliders: [{ shape: { type: "cuboid", halfExtents: { x: 1.8, y: 0.6, z: 1.8 } }, layer: "ground", tag: "stone" }] });
  out.push({ id: "post", kind: "fixed", position: { x: 6, y: 1.5, z: -5 }, colliders: [{ shape: { type: "cylinder", halfHeight: 1.5, radius: 0.4 }, layer: "ground", tag: "wood" }] });
  out.push({ id: "boulder", kind: "fixed", position: { x: -7, y: 0.4, z: 4 }, colliders: [{ shape: { type: "ball", radius: 1.1 }, layer: "ground", tag: "stone" }] });
  out.push({ id: "log", kind: "fixed", position: { x: 5, y: 0.9, z: 6 }, rotation: { x: 0, y: 0, z: Math.SQRT1_2, w: Math.SQRT1_2 }, colliders: [{ shape: { type: "capsule", halfHeight: 2, radius: 0.35 }, layer: "ground", tag: "wood" }] });
  // a wedge ramp as a triangle mesh: rises from 0.2 to 1.6 m along +x
  const rv = [-2, 0.2, -1.5, 2, 1.6, -1.5, 2, 1.6, 1.5, -2, 0.2, 1.5, 2, 0, -1.5, 2, 0, 1.5];
  out.push({ id: "ramp", kind: "fixed", position: { x: -8, y: 0, z: -7 }, colliders: [{ shape: { type: "trimesh", vertices: rv, indices: [0, 2, 1, 0, 3, 2, 1, 2, 5, 1, 5, 4] }, layer: "ground", tag: "wood" }] });

  // crates stacked on the plinth (top at 1.2 m): 3 × 3 columns, 3 high
  const crate: PrimitiveShape = { type: "cuboid", halfExtents: { x: 0.35, y: 0.35, z: 0.35 } };
  let n = 0;
  for (let level = 0; level < 3; level++)
    for (let i = 0; i < 3; i++)
      for (let j = 0; j < 3; j++) out.push(prop(`crate-${n++}`, crate, { x: (i - 1) * 0.9, y: 1.2 + 0.36 + level * 0.72, z: (j - 1) * 0.9 }));
  // a rain of mixed shapes over the field
  const drops: PrimitiveShape[] = [
    { type: "ball", radius: 0.3 },
    { type: "capsule", halfHeight: 0.3, radius: 0.25 },
    { type: "cylinder", halfHeight: 0.3, radius: 0.3 },
    { type: "cuboid", halfExtents: { x: 0.4, y: 0.2, z: 0.3 } },
  ];
  for (let k = 0; k < 24; k++) {
    const a = k * 2.399963; // golden angle spiral
    const r = 3 + 0.35 * k;
    out.push(prop(`drop-${k}`, drops[k % 4]!, { x: Math.cos(a) * r, y: 3 + (k % 6) * 0.8, z: Math.sin(a) * r }));
  }
  out.push({
    id: "pusher",
    kind: "kinematic",
    position: { x: PUSHER.start.x, y: groundHeight(PUSHER.start.x, PUSHER.start.z) + PUSHER.halfHeight + PUSHER.radius, z: PUSHER.start.z },
    colliders: [{ shape: { type: "capsule", halfHeight: PUSHER.halfHeight, radius: PUSHER.radius }, layer: "pusher" }],
  });
  return out;
}

function prop(id: string, shape: PrimitiveShape, position: Vec3): BodySpec {
  return { id, kind: "dynamic", position, colliders: [{ shape, layer: "prop", friction: 0.7, density: 1 }], angularDamping: 0.2 };
}

/** The lab's action map (the spike's values). The lab reads only `move`. */
export const LAB_ACTIONS: ActionMap = {
  sticks: { move: { up: ["KeyW", "ArrowUp"], down: ["KeyS", "ArrowDown"], left: ["KeyA", "ArrowLeft"], right: ["KeyD", "ArrowRight"], vectors: ["PadLeft", "Touch:move"] } },
};

/** The lab's share of the one kernel game: its module (systems, save slice) and content (entities and bodies). */
function labParts(collision: CollisionWorld, rng: RngStreams): { module: GameModule<GameEvents>; content: ContentPack<GameEvents> } {
  const intent = { x: 0, z: 0 }; // written by the host every step before the tick, so never saved
  const wind = rng.stream(WIND_STREAM);

  const module: GameModule<GameEvents> = {
    id: "sim-test",
    version: "0.1.0",
    setup(ctx) {
      ctx.registerSystem(
        ({ world, dt }) => {
          for (const e of world.query(PUSHER_C)) {
            const p = world.getComponent<Pusher>(e, PUSHER_C)!;
            const k = Math.min(1, PUSHER.accel * dt);
            p.vx += (intent.x * PUSHER.speed - p.vx) * k;
            p.vz += (intent.z * PUSHER.speed - p.vz) * k;
            const lim = PEN - 1;
            p.x = Math.max(-lim, Math.min(lim, p.x + p.vx * dt));
            p.z = Math.max(-lim, Math.min(lim, p.z + p.vz * dt));
            // stand on whatever ground is below (terrain, plinth, ramp): a ray down on the ground layer
            const hit = collision.castRay({ x: p.x, y: 20, z: p.z }, { x: 0, y: -1, z: 0 }, 40, { layers: ["ground"] });
            const y = (hit ? hit.point.y : 0) + PUSHER.halfHeight + PUSHER.radius;
            collision.moveKinematic(world.getComponent<Physical>(e, PHYSICAL_C)!.body, { x: p.x, y, z: p.z });
          }
        },
        { name: "sim: pusher", order: 0 },
      );
      ctx.registerSystem(
        ({ world }) => {
          for (const e of world.query(WIND_C)) {
            const w = world.getComponent<Wind>(e, WIND_C)!;
            if (--w.wait > 0) continue;
            w.wait = WIND.every;
            w.shoves++;
            // the planted defect sits here, beside the draw it breaks
            const r = SIM_FAULTS.mathRandom ? Math.random : () => wind.next();
            const props = collision.ids().filter((id) => id.startsWith("crate-") || id.startsWith("drop-"));
            const target = props[Math.floor(r() * props.length)]!;
            const a = r() * Math.PI * 2;
            collision.applyImpulse(target, { x: Math.cos(a) * WIND.impulse, y: WIND.impulse * 0.5, z: Math.sin(a) * WIND.impulse });
          }
        },
        { name: "sim: wind", order: 10 },
      );
      ctx.registerSystem(() => collision.step(), { name: "sim: physics step", order: 100 });
      ctx.registerSaveSlice({
        serialize: () => ({
          pushers: ctx.world.componentEntries<Pusher>(PUSHER_C).map(([e, p]) => [e, { ...p }]),
          winds: ctx.world.componentEntries<Wind>(WIND_C).map(([e, w]) => [e, { ...w }]),
        }),
        deserialize: (data) => {
          const d = data as { pushers: [number, Pusher][]; winds: [number, Wind][] };
          for (const [e, p] of d.pushers) Object.assign(ctx.world.getComponent<Pusher>(e, PUSHER_C)!, p);
          for (const [e, w] of d.winds) Object.assign(ctx.world.getComponent<Wind>(e, WIND_C)!, w);
        },
      });
      ctx.registerAction("sim/intent", (payload) => {
        const { x, z } = payload as { x: number; z: number };
        intent.x = x;
        intent.z = z;
      });
    },
  };

  const content: ContentPack<GameEvents> = {
    meta: { id: "sim-test-scene", displayName: "Sim test scene", version: "0.1.0" },
    requiresModules: [{ id: "sim-test", versionRange: "^0.1.0" }],
    build({ world }) {
      for (const b of sceneBodies()) {
        collision.add(b);
        const e = world.createEntity();
        world.addComponent<Physical>(e, PHYSICAL_C, { body: b.id });
        if (b.id === "pusher") world.addComponent<Pusher>(e, PUSHER_C, { x: PUSHER.start.x, z: PUSHER.start.z, vx: 0, vz: 0 });
      }
      world.addComponent<Wind>(world.createEntity(), WIND_C, { wait: WIND.every, shoves: 0 });
    },
  };

  return { module, content };
}

/** The A1 lab scene (`?scene=lab`): the deterministic Rapier pen the A1.3 replay spec runs on. */
export const LAB: SceneDef = {
  name: "lab",
  layers: LAYERS,
  seed: SIM_SEED,
  actions: LAB_ACTIONS,
  setup(collision, rng, actions) {
    const { module, content } = labParts(collision, rng);
    return {
      parts: { modules: [module], content },
      hashed: HASHED,
      bodies: sceneBodies(),
      beforeTick(game) {
        const m = actions.stick("move");
        game.dispatch("sim/intent", { x: m.x, z: -m.y }); // stick up = forward = -z
      },
    };
  },
};
